import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const STYLES = {
    title: {
        font: { bold: true, size: 16, color: { argb: "FF2F75B5" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFDDEEFF" } },
        alignment: { horizontal: "center", vertical: "middle" },
    },
    filterLine: {
        font: { size: 11 },
        alignment: { horizontal: "left", vertical: "middle" },
    },
    generatedAt: {
        font: { italic: true, size: 10, color: { argb: "FF555555" } },
        alignment: { horizontal: "left", vertical: "middle" },
    },
    header: {
        font: { bold: true, color: { argb: "FFFFFFFF" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF2F75B5" } },
        alignment: { vertical: "middle", horizontal: "center" },
    },
    data: {
        font: { size: 10 },
        alignment: { vertical: "middle", horizontal: "left", wrapText: true },
    },
    dataCenter: {
        font: { size: 10 },
        alignment: { vertical: "middle", horizontal: "center" },
    },
    zebra: {
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } },
    },
    statusApproved: {
        font: { bold: true, color: { argb: "FF375623" } },
    },
    statusPending: {
        font: { bold: true, color: { argb: "FFBF8F00" } },
    },
    statusRejected: {
        font: { bold: true, color: { argb: "FFC00000" } },
    },
};

const border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
};

function applyStyle(cell, style) {
    if (style.font) cell.font = { ...cell.font, ...style.font };
    if (style.fill) cell.fill = style.fill;
    if (style.alignment) cell.alignment = style.alignment;
}

const pad = (n) => String(n).padStart(2, "0");

const nowStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const STATUS_STYLES = {
    APPROVED: STYLES.statusApproved,
    PENDING: STYLES.statusPending,
    REJECTED: STYLES.statusRejected,
};

const COLUMNS = [
    { header: "No.",            key: "no",                  width: 6  },
    { header: "Request Ref.",   key: "requestReference",    width: 22 },
    { header: "Request Type",   key: "requestType",         width: 18 },
    { header: "Change Type",    key: "changeType",          width: 22 },
    { header: "Customer ID",    key: "customerId",          width: 14 },
    { header: "Customer Name",  key: "customerName",        width: 28 },
    { header: "Old Value",      key: "oldValue",            width: 32 },
    { header: "New Value",      key: "newValue",            width: 32 },
    { header: "Status",         key: "status",              width: 14 },
    { header: "Requested By",   key: "requestedByName",     width: 24 },
    { header: "Branch",         key: "requestedBranchName", width: 26 },
    { header: "Actioned By",    key: "actionByName",        width: 24 },
    { header: "Created At",     key: "createdAt",           width: 22 },
    { header: "Updated At",     key: "updatedAt",           width: 22 },
];

const COL_COUNT = COLUMNS.length;

/**
 * Generates an Excel workbook for the Monitor Customer Details report.
 *
 * @param {Array}  data    - Array of MonitorCustomerDetailsReportDto objects returned from the API.
 * @param {Object} filters - { fromDate, toDate, changeType, branch } used for the header section.
 */
async function generateMonitorCustomerDetailsExcel(data, filters = {}) {
    if (!Array.isArray(data) || data.length === 0) {
        console.warn("No data to export for Monitor Customer Details report.");
        return;
    }

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Monitor Customer Details");

    // ---- Column widths ----
    const refLen = data.reduce(
        (max, item) => Math.max(max, String(item.requestReference ?? "").length),
        String(COLUMNS[1].header).length
    );
    COLUMNS.forEach((col, i) => {
        ws.getColumn(i + 1).width =
            col.key === "requestReference" ? refLen + 1 : col.width;
    });

    // ---- Row 1 : Title ----
    ws.mergeCells(1, 1, 1, COL_COUNT);
    const titleCell = ws.getCell("A1");
    titleCell.value = "MONITOR CUSTOMER DETAILS REPORT";
    applyStyle(titleCell, STYLES.title);
    ws.getRow(1).height = 40;

    // ---- Row 2 : Filter summary ----
    ws.mergeCells(2, 1, 2, COL_COUNT);
    const filterCell = ws.getCell("A2");
    const fromLabel      = filters.fromDate   || "-";
    const toLabel        = filters.toDate     || "-";
    const branchLabel    = filters.branch     || "All";
    const changeLabel    = filters.changeType || "All";
    filterCell.value = `Period: ${fromLabel}  to  ${toLabel}     |     Branch: ${branchLabel}     |     Change Type: ${changeLabel}`;
    applyStyle(filterCell, STYLES.filterLine);
    ws.getRow(2).height = 20;

    // ---- Row 3 : Generated at ----
    ws.mergeCells(3, 1, 3, COL_COUNT);
    const genCell = ws.getCell("A3");
    genCell.value = `Report Generated: ${nowStr()}`;
    applyStyle(genCell, STYLES.generatedAt);
    ws.getRow(3).height = 16;

    // ---- Row 4 : Blank spacer ----
    ws.addRow([]);

    // ---- Row 5 : Table headers ----
    const headerRow = ws.addRow(COLUMNS.map((c) => c.header));
    headerRow.eachCell((cell) => {
        cell.border = border;
        applyStyle(cell, STYLES.header);
    });
    headerRow.height = 22;

    // ---- Data rows (starting from row 6) ----
    data.forEach((item, idx) => {
        const isZebra = idx % 2 === 1;

        const rowValues = COLUMNS.map((col) => {
            const v = item[col.key];
            return v !== null && v !== undefined ? v : "-";
        });

        const dataRow = ws.addRow(rowValues);
        dataRow.height = 20;

        dataRow.eachCell((cell, colNum) => {
            cell.border = border;
            const colKey = COLUMNS[colNum - 1]?.key;

            if (colKey === "status") {
                applyStyle(cell, STYLES.dataCenter);
                const upperStatus = String(item.status || "").toUpperCase();
                const sStyle = STATUS_STYLES[upperStatus];
                if (sStyle) applyStyle(cell, sStyle);
            } else if (colKey === "no" || colKey === "customerId") {
                applyStyle(cell, STYLES.dataCenter);
            } else {
                applyStyle(cell, STYLES.data);
            }

            if (isZebra && colKey !== "status") {
                cell.fill = STYLES.zebra.fill;
            }
        });
    });

    // ---- Freeze panes at header row ----
    ws.views = [{ state: "frozen", xSplit: 0, ySplit: 5 }];

    // ---- Export ----
    const buffer = await workbook.xlsx.writeBuffer();
    const now = new Date();
    const fileName = `MonitorCustomerDetails_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;
    saveAs(
        new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName
    );
}

export default generateMonitorCustomerDetailsExcel;
